import {
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  NgZone,
  signal,
  DestroyRef,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { WfNotificationService } from './services/notifications.service';
import { WfNotificationToastComponent } from './notification-toast.component';
import { WfNotificationsStackItemDirective } from './directives/stack-item.directive';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

@Component({
  selector: 'wf-notifications-pile',
  imports: [WfNotificationToastComponent, WfNotificationsStackItemDirective],
  template: `
    <div
      class="wf-notifications-container"
      [class.wf-notifications-container--is-paused]="isPaused()"
      [style.--max-visible-count]="maxVisibleCount()"
      (mouseenter)="setPaused(true)"
      (mouseleave)="setPaused(false)"
    >
      @for (
        toast of toasts();
        track toast.id;
        let i = $index;
        let last = $last;
        let count = $count
      ) {
        <div
          class="wf-notifications-pile__notification-item"
          [class.wf-notifications-pile__notification-item--hidden]="
            i < count - maxVisibleCount()
          "
          [wfNotificationsStackItem]="count - i - 1"
          animate.enter="wf-notifications-pile__notification-item--enter"
          animate.leave="wf-notifications-pile__notification-item--leave"
        >
          <wf-notification-toast
            [toast]="toast"
            [paused]="isPaused()"
            [isLast]="last"
            (dismiss)="removeToast($event)"
          />
        </div>
      }
    </div>
  `,
  styles: [
    `
      .wf-notifications-container {
        position: fixed;
        bottom: var(--wf-spacer-lg);
        right: var(--wf-spacer-sm);
        z-index: 9999;
        display: flex;
        flex-direction: column;
        gap: 0;
        pointer-events: auto;
        --stack-step: 10px;
        --hidden-toast-height: 45px;

        &--is-paused {
          gap: var(--wf-spacer-xs);
        }
      }

      .wf-notifications-pile__notification-item {
        transition:
          margin-bottom 200ms ease,
          transform 200ms ease,
          opacity 200ms ease,
          visibility 200ms ease;
        will-change: margin-bottom, transform, opacity, visibility;

        &--hidden {
          opacity: 0;
          visibility: hidden;
          pointer-events: none;
        }

        /*
         * The stacking rules below also drive transform, so the enter/leave
         * keyframes have to win over them for their whole run — hence
         * animations rather than a transition on a toggled class.
         */
        &--enter {
          animation: wf-toast-enter 300ms ease-out;
        }

        &--leave {
          animation: wf-toast-leave 200ms ease-in forwards;
        }
      }

      @keyframes wf-toast-enter {
        from {
          transform: translateY(300px);
          opacity: 0;
        }
      }

      @keyframes wf-toast-leave {
        to {
          transform: translateY(300px);
          opacity: 0;
        }
      }

      /* Hides all but the last toast */
      .wf-notifications-container:not(.wf-notifications-container--is-paused)
        .wf-notifications-pile__notification-item:not(:last-child) {
        margin-bottom: calc(-1 * var(--hidden-toast-height));
      }

      /* Dynamic layered offsets using directive-provided --depth */
      .wf-notifications-container:not(.wf-notifications-container--is-paused)
        .wf-notifications-pile__notification-item {
        transform: translateY(
          calc(var(--depth) * calc(-1 * var(--stack-step)))
        );
      }
    `,
  ],
})
export class WfNotificationsPileComponent {
  private readonly notificationService = inject(WfNotificationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  readonly toasts = this.notificationService.toasts;
  readonly toasts$ = toObservable(this.notificationService.toasts);
  readonly isPaused = signal<boolean>(false);

  // Calculate max visible notifications based on screen height
  readonly maxVisibleCount = computed(() => {
    const allToasts = this.toasts();
    if (!allToasts.length) return 0;

    const maxNotificationHeight = 158; // Assuming max notification height of 150px + 8px spacing
    const bottomSpacing = 24;
    const availableHeight = window.innerHeight - bottomSpacing;
    return Math.max(1, Math.floor(availableHeight / maxNotificationHeight));
  });

  constructor() {
    this.toasts$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((list) => {
      if (!list.length) return;

      const last = list.at(-1);
      if (last.onActivateTick) {
        this.ngZone.run(() => this.cdr.detectChanges());
      }
    });

    // Clear all notifications on route change
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.notificationService.clear();
      });
  }

  removeToast(id: number): void {
    this.notificationService.remove(id);
  }

  setPaused(paused: boolean): void {
    this.isPaused.set(paused);

    if (paused) {
      this.notificationService.pause();
      return;
    }

    this.notificationService.resume();
  }
}
